import { clearAdminHeaderBrand } from '@/utils/tenantBranding'
import { useUserStore } from '@/stores/modules/user.js'
import { useStore as useLayoutRouterStore } from '@/stores/modules/router.js'
import { getLoginUserFromSessionPayload } from '@/utils/loginUser'

export const PORTAL_TENANT = 'tenant'
export const PORTAL_PLATFORM = 'platform'

const ROLE_PLATFORM_ADMIN = 100

const PORTAL_PREFIX = {
  [PORTAL_TENANT]: 'tenantAdmin:',
  [PORTAL_PLATFORM]: 'platformAdmin:',
}

const PORTAL_KEYS = ['token', 'user', 'tenantId', 'tenantCode']

function portalPrefix(portal) {
  return PORTAL_PREFIX[portal] || PORTAL_PREFIX[PORTAL_TENANT]
}

function readPortalKey(portal, key) {
  return localStorage.getItem(`${portalPrefix(portal)}${key}`)
}

function writePortalKey(portal, key, value) {
  if (value == null || value === '') {
    localStorage.removeItem(`${portalPrefix(portal)}${key}`)
    return
  }
  localStorage.setItem(`${portalPrefix(portal)}${key}`, value)
}

function clearLegacySharedKeys() {
  localStorage.removeItem('token')
  localStorage.removeItem('user')
  localStorage.removeItem('users')
  localStorage.removeItem('tenantId')
  localStorage.removeItem('isPlatformAdmin')
  localStorage.removeItem('userInfo')
}

/** 当前 hash 路由对应的门户（决定读哪套 token） */
export function getPortalForRoute(path = '/') {
  const normalized = path.split('?')[0] || '/'
  if (normalized === '/superadmin/login' || normalized.startsWith('/platform')) {
    return PORTAL_PLATFORM
  }
  return PORTAL_TENANT
}

export function getPortalForCurrentRoute() {
  const hash = window.location.hash || ''
  const path = hash.replace(/^#/, '') || '/'
  return getPortalForRoute(path)
}

export function hasPortalSession(portal) {
  return Boolean(readPortalKey(portal, 'token'))
}

export function getPortalSession(portal) {
  const token = readPortalKey(portal, 'token')
  if (!token) {
    return null
  }
  let session = null
  try {
    const raw = readPortalKey(portal, 'user')
    session = raw ? JSON.parse(raw) : null
  } catch {
    session = null
  }
  return {
    portal,
    token,
    session,
    tenantId: readPortalKey(portal, 'tenantId'),
    tenantCode: readPortalKey(portal, 'tenantCode'),
  }
}

export function getActivePortalSession() {
  const portal = getPortalForCurrentRoute()
  const session = getPortalSession(portal)
  if (session) {
    return session
  }
  const fallback = portal === PORTAL_PLATFORM ? PORTAL_TENANT : PORTAL_PLATFORM
  return getPortalSession(fallback)
}

function syncLegacyActiveSession(portal) {
  const stored = getPortalSession(portal)
  if (!stored) {
    return
  }
  localStorage.setItem('token', stored.token)
  localStorage.setItem('user', JSON.stringify(stored.session))
  if (stored.tenantId) {
    localStorage.setItem('tenantId', stored.tenantId)
  } else {
    localStorage.removeItem('tenantId')
  }
  localStorage.setItem('isPlatformAdmin', portal === PORTAL_PLATFORM ? '1' : '0')
}

export function savePortalSession(portal, payload = {}, token) {
  const authToken = token || payload.token
  if (!authToken) {
    return
  }
  writePortalKey(portal, 'token', authToken)
  writePortalKey(portal, 'user', JSON.stringify(payload))
  if (payload.tenantId != null) {
    writePortalKey(portal, 'tenantId', String(payload.tenantId))
  }
  if (payload.tenantCode != null) {
    writePortalKey(portal, 'tenantCode', String(payload.tenantCode))
  }
  syncLegacyActiveSession(portal)
  try {
    useUserStore().setUser(payload, authToken)
  } catch (_) {
    // pinia 未初始化时忽略
  }
}

export function clearPortalSession(portal) {
  PORTAL_KEYS.forEach((key) => writePortalKey(portal, key, null))
  if (getPortalForCurrentRoute() === portal) {
    clearLegacySharedKeys()
  }
  clearLayoutMenuState()
  try {
    useUserStore().clearUser()
  } catch (_) {
    // pinia 未初始化时忽略
  }
}

export function clearAllPortalSessions() {
  ;[PORTAL_TENANT, PORTAL_PLATFORM].forEach((portal) => {
    PORTAL_KEYS.forEach((key) => writePortalKey(portal, key, null))
  })
  clearLegacySharedKeys()
  clearAdminHeaderBrand()
  clearLayoutMenuState()
  try {
    useUserStore().clearUser()
  } catch (_) {
    // pinia 未初始化时忽略
  }
}

/** @deprecated 兼容旧调用，等价于 clearAllPortalSessions */
export function clearAuthSession(options = {}) {
  const { keepTenantCode = true, keepSavedUsernames = true } = options
  const tenantCode = keepTenantCode ? readPortalKey(PORTAL_TENANT, 'tenantCode') : null
  clearAllPortalSessions()
  if (keepTenantCode && tenantCode) {
    writePortalKey(PORTAL_TENANT, 'tenantCode', tenantCode)
  }
  if (!keepSavedUsernames) {
    localStorage.removeItem('adminLoginUsername')
    localStorage.removeItem('platformAdminUsername')
  }
}

export function hasAuthSession() {
  return hasPortalSession(PORTAL_TENANT) || hasPortalSession(PORTAL_PLATFORM)
}

function readLoginRoleId(session) {
  const user = getLoginUserFromSessionPayload(session)
  return Number(user?.roleId ?? user?.role_id ?? 0)
}

export function resolvePortalFromSession(portal = getPortalForCurrentRoute()) {
  const stored = getPortalSession(portal)
  if (!stored) {
    return null
  }
  const session = stored.session
  if (session?.platformAdmin === true) {
    return PORTAL_PLATFORM
  }
  const roleId = readLoginRoleId(session)
  if (roleId === ROLE_PLATFORM_ADMIN) {
    return PORTAL_PLATFORM
  }
  if (roleId > 0) {
    return portal
  }
  return portal
}

export function persistAuthPortal(_portal) {
  // 门户类型已由分仓 storage 表达，保留空实现兼容旧调用
}

export function syncAuthPortalFlag() {
  // 门户类型已由分仓 storage 表达，保留空实现兼容旧调用
}

export function isPlatformAdminSession() {
  return getPortalForCurrentRoute() === PORTAL_PLATFORM && hasPortalSession(PORTAL_PLATFORM)
}

export function getActivePortal() {
  return hasPortalSession(PORTAL_PLATFORM) ? PORTAL_PLATFORM
    : hasPortalSession(PORTAL_TENANT) ? PORTAL_TENANT
    : null
}

export function clearLayoutMenuState() {
  localStorage.removeItem('getRouter')
  try {
    useLayoutRouterStore().getClearData()
  } catch (_) {
    // pinia 未初始化时忽略
  }
}

let legacyMigrated = false

/** 将旧版共用 token/user 迁移到分仓 storage（仅执行一次） */
export function migrateLegacySessionIfNeeded() {
  if (legacyMigrated) {
    return
  }
  legacyMigrated = true
  const legacyToken = localStorage.getItem('token')
  if (!legacyToken) {
    return
  }
  const legacyUser = localStorage.getItem('user')
  let payload = null
  try {
    payload = legacyUser ? JSON.parse(legacyUser) : null
  } catch {
    payload = null
  }
  const portal = localStorage.getItem('isPlatformAdmin') === '1'
    ? PORTAL_PLATFORM
    : PORTAL_TENANT
  if (!hasPortalSession(portal)) {
    savePortalSession(
      portal,
      payload || {},
      legacyToken,
    )
    if (localStorage.getItem('tenantId')) {
      writePortalKey(portal, 'tenantId', localStorage.getItem('tenantId'))
    }
    if (localStorage.getItem('tenantCode')) {
      writePortalKey(portal, 'tenantCode', localStorage.getItem('tenantCode'))
    }
  }
  clearLegacySharedKeys()
}

export function resolveLoginPortalAccess(targetPath) {
  migrateLegacySessionIfNeeded()

  if (targetPath === '/login') {
    if (hasPortalSession(PORTAL_TENANT)) {
      return { allowLoginPage: false, redirectTo: '/home' }
    }
    return { allowLoginPage: true, redirectTo: null }
  }

  if (targetPath === '/superadmin/login') {
    if (hasPortalSession(PORTAL_PLATFORM)) {
      return { allowLoginPage: false, redirectTo: '/platform/tenants' }
    }
    return { allowLoginPage: true, redirectTo: null }
  }

  return { allowLoginPage: true, redirectTo: null }
}

export function isLoginResponsePlatformAdmin(data = {}) {
  const roleId = Number(data.users?.roleId ?? data.users?.role_id ?? 0)
  return roleId === ROLE_PLATFORM_ADMIN || data.platformAdmin === true
}

export function getRequestAuth() {
  migrateLegacySessionIfNeeded()
  const portal = getPortalForCurrentRoute()
  return getPortalSession(portal)
}

const LAST_PORTAL_KEY = 'adminActivePortal'

export function activatePortalSession(portal) {
  const stored = getPortalSession(portal)
  if (!stored) {
    return
  }
  const lastPortal = sessionStorage.getItem(LAST_PORTAL_KEY)
  if (lastPortal && lastPortal !== portal) {
    clearLayoutMenuState()
  }
  sessionStorage.setItem(LAST_PORTAL_KEY, portal)
  syncLegacyActiveSession(portal)
  try {
    useUserStore().setUser(stored.session, stored.token)
  } catch (_) {
    // pinia 未初始化时忽略
  }
}
